import React, { useState, useEffect, use } from "react";

const Calendar = (props) => {
    const {
        selectedDate,
        setSelectedDate,
        rangeStart,
        rangeEnd,
        setRangeStart,
        setRangeEnd
    } = props;
    const isRangeMode = typeof setRangeStart === "function" && typeof setRangeEnd === "function";
    const activeDate = isRangeMode ? rangeStart || rangeEnd : selectedDate;
    const handleCalendarSelect = (dateStr) => {
        if (isRangeMode) {
            if (!rangeStart || rangeEnd || dateStr < rangeStart) {
                setRangeStart(dateStr);
                setRangeEnd("");
            } else {
                setRangeEnd(dateStr);
                setShowCalendar(false);
            }
            return;
        }

        setSelectedDate(dateStr);
        setShowCalendar(false);
    };
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const selectMonthRange = (monthOffset) => {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
        const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
        setRangeStart(formatDate(first));
        setRangeEnd(formatDate(last));
        setCalendarViewDate({ year: first.getFullYear(), month: first.getMonth() });
        setShowCalendar(false);
    };
    const selectAlwaysRange = () => {
        const today = new Date();
        setRangeStart("");
        setRangeEnd(formatDate(today));
        setCalendarViewDate({ year: today.getFullYear(), month: today.getMonth() });
        setShowCalendar(false);
    };
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    // Ref per l'input del date picker
    const dateInputRef = React.useRef(null);

    // Stato per il calendario modal
    const [showCalendar, setShowCalendar] = React.useState(false);
    const [calendarViewDate, setCalendarViewDate] = React.useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });

    const { year, month } = calendarViewDate;
    const daysInMonth = getDaysInMonth(year, month);
    const currentYear = new Date().getFullYear();
    const startYear = Math.min(currentYear - 5, year - 5);
    const endYear = Math.max(currentYear, year);
    const yearOptions = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);
    // Monday-first: shift so Monday=0
    let firstDay = getFirstDayOfMonth(year, month);
    firstDay = (firstDay + 6) % 7; // convert Sun=0 to Mon=0
    const monthLabel = new Date(year, month).toLocaleString("it-IT", { month: "long", year: "numeric" });
    const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
    const monthNames = [
        "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
        "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
    ];
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    return (
        <>
            {!isRangeMode && (
                <input
                    ref={dateInputRef}
                    className="form-control date-picker"
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    style={{ maxWidth: 140, height: 1, display: "none" }}
                />
            )}

            <div className={`date-switcher${isRangeMode ? " date-switcher-range" : ""}`}>
                {!isRangeMode && (
                    <button
                        type="button"
                        className="date-switcher-btn"
                        aria-label="Giorno precedente"
                        onClick={e => {
                            e.preventDefault();
                            const prevDate = new Date(selectedDate);
                            prevDate.setDate(prevDate.getDate() - 1);
                            setSelectedDate(prevDate.toLocaleDateString("en-CA"));
                        }}
                    >
                        <span className="material-symbols-outlined">chevron_left</span>
                    </button>
                )}
                <button
                    type="button"
                    className="date-switcher-label"
                    aria-label={isRangeMode ? "Seleziona intervallo di date" : undefined}
                    onClick={e => {
                        e.preventDefault();
                        const dateToShow = activeDate
                            ? new Date(`${activeDate}T00:00:00`)
                            : new Date();
                        const y = dateToShow.getFullYear();
                        const m = dateToShow.getMonth();
                        setCalendarViewDate({ year: y, month: m });
                        setShowCalendar(true);
                    }}
                >
                    <span className="material-symbols-outlined">calendar_today</span>
                    <span>
                        {isRangeMode
                            ? (rangeStart || rangeEnd
                                ? `${rangeStart ? new Date(`${rangeStart}T00:00:00`).toLocaleDateString("it-IT") : rangeEnd ? "Inizio" : "Da"} – ${rangeEnd ? new Date(`${rangeEnd}T00:00:00`).toLocaleDateString("it-IT") : "A"}`
                                : "Intervallo date")
                            : new Date(`${selectedDate}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}
                    </span>
                </button>
                {!isRangeMode && (
                    <button
                        type="button"
                        className="date-switcher-btn"
                        aria-label="Giorno successivo"
                        onClick={e => {
                            e.preventDefault();
                            const nextDate = new Date(selectedDate);
                            nextDate.setDate(nextDate.getDate() + 1);
                            setSelectedDate(nextDate.toLocaleDateString("en-CA"));
                        }}
                    >
                        <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                )}
            </div>

            {showCalendar && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(0,0,0,0.85)", zIndex: 9999,
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
                    }}
                    onClick={() => setShowCalendar(false)}
                >
                    <div
                        style={{
                            background: "var(--dark-surface, var(--light-surface))", border: "1px solid var(--dark-border, var(--light-border))", color: "var(--dark-text, var(--light-text))", borderRadius: 16, padding: "24px 16px",
                            width: "95vw", maxWidth: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.4)"
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                            <button
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                    let m = month - 1, y = year;
                                    if (m < 0) { m = 11; y--; }
                                    setCalendarViewDate({ year: y, month: m });
                                }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>navigate_before</span>
                            </button>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flex: 1, margin: "0 12px" }}>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <select
                                        className="form-control form-control-sm"
                                        value={month}
                                        onChange={e => setCalendarViewDate({ year, month: Number(e.target.value) })}
                                        style={{ maxWidth: 120 }}
                                    >
                                        {monthNames.map((name, idx) => (
                                            <option key={name} value={idx}>{name}</option>
                                        ))}
                                    </select>
                                    <select
                                        className="form-control form-control-sm"
                                        value={year}
                                        onChange={e => setCalendarViewDate({ year: Number(e.target.value), month })}
                                        style={{ maxWidth: 90 }}
                                    >
                                        {yearOptions.map(optionYear => (
                                            <option key={optionYear} value={optionYear}>{optionYear}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <button
                                className="btn btn-sm btn-light"
                                onClick={() => {
                                    let m = month + 1, y = year;
                                    if (m > 11) { m = 0; y++; }
                                    setCalendarViewDate({ year: y, month: m });
                                }}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>navigate_next</span>
                            </button>
                        </div>
                        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "center" }}>
                            <thead>
                                <tr>
                                    {dayNames.map(d => (
                                        <th key={d} style={{ padding: "4px 0", fontSize: 13, color: "var(--dark-text-muted, var(--light-text-muted))", fontWeight: 600 }}>{d}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {weeks.map((week, wi) => (
                                    <tr key={wi}>
                                        {week.map((day, di) => {
                                            if (!day) return <td key={di} />;
                                            const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                                            const isSelected = dateStr === selectedDate;
                                            const isRangeStart = isRangeMode && dateStr === rangeStart;
                                            const isRangeEnd = isRangeMode && dateStr === rangeEnd;
                                            const isInRange = isRangeMode && rangeStart && rangeEnd && dateStr > rangeStart && dateStr < rangeEnd;
                                            const isHighlighted = isRangeStart || isRangeEnd || (!isRangeMode && isSelected);
                                            const isWeekend = di >= 5;
                                            return (
                                                <td key={di} style={{ padding: 2 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCalendarSelect(dateStr)}
                                                        style={{
                                                            width: 40, height: 40, borderRadius: "50%", border: "none",
                                                            background: isHighlighted
                                                                ? "var(--dark-accent, var(--light-accent))"
                                                                : isInRange ? "rgba(59, 130, 246, 0.18)" : "transparent",
                                                            color: isHighlighted ? "#fff" : isWeekend ? "#ef4444" : "var(--dark-text, var(--light-text))",
                                                            fontWeight: isHighlighted ? 700 : 400,
                                                            cursor: "pointer", fontSize: 15
                                                        }}
                                                    >
                                                        {day}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ display: isRangeMode ? "flex" : undefined, justifyContent: "center", gap: 4, flexWrap: "nowrap", marginTop: 16 }}>
                            {isRangeMode && (
                                <>
                                    <button type="button" className="btn btn-sm btn-outline-primary" style={{ flex: "1 1 0", minWidth: 0, padding: "4px 1px", fontSize: 11, whiteSpace: "nowrap" }} onClick={selectAlwaysRange}>
                                        Sempre
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-primary" style={{ flex: "1 1 0", minWidth: 0, padding: "4px 1px", fontSize: 11, whiteSpace: "nowrap" }} onClick={() => selectMonthRange(-1)}>
                                        Scorso mese
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-primary" style={{ flex: "1 1 0", minWidth: 0, padding: "4px 1px", fontSize: 11, whiteSpace: "nowrap" }} onClick={() => selectMonthRange(0)}>
                                        Questo mese
                                    </button>
                                </>
                            )}
                            <button
                                className="btn btn-sm btn-outline-primary"
                                style={isRangeMode ? { flex: "1 1 0", minWidth: 0, padding: "4px 1px", fontSize: 11, whiteSpace: "nowrap" } : undefined}
                                onClick={() => {
                                    const now = new Date();
                                    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
                                    handleCalendarSelect(today);
                                    setCalendarViewDate({ year: now.getFullYear(), month: now.getMonth() });
                                }}
                            >
                                Oggi
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
export default Calendar;